import React, { useEffect, useState, Fragment, forwardRef } from "react";
import { useItemConfigContext } from "../../hooks/useContext/useItemConfigContext";
import { useAuthContext } from "../../hooks/useContext/useAuthContext";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import ImageContainer from "../Shared/ImageContainerBox";
import ErrorBox from "../Shared/ErrorBox";
import {
  Card,
  Collapse,
  CardContent,
  Typography,
  Tooltip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  TextField,
  FormControl,
  Input,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import theme from "../../theme";

const RootCard = styled(Card)({
  height: "100%",
  backgroundColor: "white",
  p: 2,
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  overflowWrap: "break-word",
  position: "relative",
});

const CustomDelete = styled(Delete)({
  color: theme.palette.common.error,
});

const CustomEdit = styled(Edit)({
  color: theme.palette.common.offGrey,
});

const InputContainer = styled(Input)({
  padding: "6px 12px",
  margin: ".8rem",
});

const ItemConfigDetails = ({ itemConfig }) => {
  const theme = useTheme();
  const { dispatch } = useItemConfigContext();
  const { user } = useAuthContext();
  const [editedItemImage, setEditedItemImage] = useState(null);
  const [editedItemIncludes, setEditedItemIncludes] = useState(itemConfig.itemIncludes ? itemConfig.itemIncludes : "");
  const [editedItemAvailable, setEditedItemAvailable] = useState(itemConfig.itemAvailable ? itemConfig.itemAvailable : false);
  const [editedItemSize, setEditedItemSize] = useState(itemConfig.itemDetails.length === 2 ? "Małe i Duże" : "Jeden rozmiar");
  const [itemPrice1, setItemPrice1] = useState(itemConfig.itemDetails.length > 0 ? itemConfig.itemDetails[0].itemPrice : "");
  const [itemPrice2, setItemPrice2] = useState(itemConfig.itemDetails.length > 1 ? itemConfig.itemDetails[1].itemPrice : "");
  const [editedItemName, setEditedItemName] = useState(itemConfig.itemName ? itemConfig.itemName : "");
  const [editedItemType, setEditedItemType] = useState(itemConfig.itemType ? itemConfig.itemType : "Jedzenie");
  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedItemDetails, setExpandedItemDetails] = useState(false);
  const itemSizeOptions = ["Jeden rozmiar", "Małe i Duże"];
  const itemAvailableOptions = ["Nie", "Tak"];
  const itemTypeOptions = ["Jedzenie", "Napoje", "Napoje Alkoholowe"];
  const [itemPriceAmount, setItemPriceAmount] = useState(false);
  let newItemDetails;
  
  const [error, setError] = useState(null);
  const [emptyFields, setEmptyFields] = useState([]);
  const [itemPrice1Error, setItemPrice1Error] = useState(null);
  const [itemPrice2Error, setItemPrice2Error] = useState(null);

  const SolidLineDiv = styled("div")({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "4px",
    backgroundColor: itemConfig.itemAvailable ? theme.palette.component.componentBackground : theme.palette.common.errorDarker,
  });

  const ForwardedCollapse = forwardRef((props, ref) => (
    <Collapse ref={ref} {...props} />
  ));

  const handleExpandItemDetailsClick = () => {
    setExpandedItemDetails(!expandedItemDetails);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  //render large item price input field if item size is Małe i Duże
  useEffect(() => {
    if (editedItemSize === "Małe i Duże") {
      setItemPriceAmount(false);
      if (!itemPrice2) {
        setItemPrice2("");
      }
    } else {
      setItemPriceAmount(true);
    }
  }, [editedItemSize, itemPrice2]);

  //useEffect to check if itemPrice1 and itemPrice2 is a number
  useEffect(() => {
    if (isNaN(itemPrice1)) {
      setItemPrice1Error("Proszę wprowadzić poprawny numer");
      setEmptyFields((emptyFields) => [...emptyFields, "itemPrice1"]);
    } else {
      setItemPrice1Error(null);
      setEmptyFields((emptyFields) =>
        emptyFields.filter((field) => field !== "itemPrice1")
      );
    }
    if (isNaN(itemPrice2) && editedItemSize === "Małe i Duże") {
      setItemPrice2Error("Proszę wprowadzić poprawny numer");
      setEmptyFields((emptyFields) => [...emptyFields, "itemPrice2"]);
    } else {
      setItemPrice2Error(null);
      setEmptyFields((emptyFields) =>
        emptyFields.filter((field) => field !== "itemPrice2")
      );
    }
  }, [itemPrice1, itemPrice2, editedItemSize]);

  if (!user) {
    return null;
  }

  const handleDelete = async () => {
    const response = await fetch(
      process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/" + itemConfig._id,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}`},
      }
    );
    const json = await response.json();

    if (response.ok) {
      dispatch({ type: "DELETE_ITEMCONFIG", payload: json });
    }
  };

  //cancel button on edit
  const handleEditClick = () => {
    if (editMode === true) {
      setEditMode(false);
      setEditedItemName(itemConfig.itemName);
      setEditedItemSize(itemConfig.itemDetails.length === 2 ? "Małe i Duże" : "Jeden rozmiar");
      setItemPrice1(itemConfig.itemDetails.length > 0 ? itemConfig.itemDetails[0].itemPrice : "");
      setItemPrice2(itemConfig.itemDetails.length > 1 ? itemConfig.itemDetails[1].itemPrice : "");
      setEditedItemIncludes(!itemConfig.itemIncludes ? "" : itemConfig.itemIncludes);
      setEditedItemAvailable(itemConfig.itemAvailable);
      setEditedItemType(itemConfig.itemType ? itemConfig.itemType : "Jedzenie");
    } else if (editMode === false) {
      setEditMode(true);
    }
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    if (!user) {
      setError("Musisz być zalogowany");
      return;
    }
    let emptyFieldsClientSide = [];
    if (!editedItemName) {
      emptyFieldsClientSide.push("editedItemName");
      setError("Proszę ustawić nazwę");
    }
    if (!itemPrice1) {
      emptyFieldsClientSide.push("itemPrice1");
      setError("Proszę ustawić cenę");
    }
    if (editedItemSize === "Małe i Duże" && !itemPrice2) {
      emptyFieldsClientSide.push("itemPrice2");
      setError("Proszę ustawić cenę dla dużego item");
    }
    if (!editedItemType) {
      emptyFieldsClientSide.push("itemType");
      setError("Proszę wybrać Jedzenie, Napoje, albo Napoje Alkoholowe");
    }
    if (emptyFieldsClientSide.length > 0) {
      setEmptyFields(emptyFieldsClientSide);
      if (emptyFieldsClientSide.length > 1) {
        setError("Proszę wypełnić wszystkie pola");
      }
      return;
    }

    if (editedItemSize !== "Jeden rozmiar") {
      newItemDetails = [
        {itemPrice: parseFloat(itemPrice1), itemSize: "Małe"},
        {itemPrice: parseFloat(itemPrice2), itemSize: "Duże"}
      ];
    } else {
      newItemDetails = [
        {itemPrice: parseFloat(itemPrice1), itemSize: "Jeden rozmiar"}
      ];
    }

  try {
    const formData = new FormData();
    if (editedItemImage) {
      formData.append("file", editedItemImage);
    }
    formData.append("itemName", editedItemName);
    formData.append("itemType", editedItemType);
    formData.append('itemDetails', JSON.stringify(newItemDetails))
    formData.append("itemIncludes", editedItemIncludes);
    formData.append("itemAvailable", editedItemAvailable ? "true" : "false");

    const response = await fetch(
      process.env.REACT_APP_SERVER_URL +
        "/api/itemconfigs/update/" +
        itemConfig._id,
      {
        method: "PUT",
        body: formData,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    const json = await response.json();

    if (!response.ok) {
      setError(json.error);
      setEmptyFields(json.emptyFieldsBackEnd);
      console.log(json.error);
    }

    if (response.ok) {
      setError(null);
      setEmptyFields([]);
      setEditMode(false);
      dispatch({ type: "UPDATE_ITEMCONFIG", payload: json });
    }
  } catch (error) {
    console.error(error);
    setError("Wystąpił błąd, proszę spróbować ponownie");
  }
};

  return (
    <RootCard variant="outlined">
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton
          sx={{
            color: theme.palette.component.componentBackground,
            backgroundColor: theme.palette.component.componentBackground,
            ":hover": { bgcolor: theme.palette.common.offWhite },
            }}
          size="large"
          aria-label="Edytuj item"
          aria-haspopup="true"
          onClick={handleEditClick}
        >
          <Tooltip title={"Edytuj Item"} placement="top">
            <CustomEdit />
          </Tooltip>
        </IconButton>
        <IconButton
          sx={{
              color: theme.palette.component.componentBackground,
              backgroundColor: theme.palette.component.componentBackground,
              ":hover": { bgcolor: theme.palette.common.offWhite },
            }}
          size="large"
          aria-label="Usuń"
          aria-haspopup="true"
          ariant="outlined"
          onClick={handleClickOpen}
        >
          <Tooltip title="Usuń" placement="top">
            <CustomDelete />
          </Tooltip>
        </IconButton>
      </div>

      <CardContent onClick={handleExpandItemDetailsClick}>
        <SolidLineDiv />
        {editMode ? (
          <>
            <ImageContainer
              itemName={itemConfig.itemName}
              itemImage={editedItemImage ? URL.createObjectURL(editedItemImage) : itemConfig.itemImage}
            />
            <InputContainer
              type="file"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  setEditedItemImage(file);
                }
              }}
            />

            <TextField
              fullWidth
              required={true}
              label="Nazwa Item"
              autoComplete="Nazwa Item"
              type="text"
              onChange={(e) => setEditedItemName(e.target.value)}
              value={editedItemName}
              sx={{ marginTop: "0.5rem" }}
              error={emptyFields && emptyFields.includes("editedItemName")}
            />

            <FormControl
              required={true}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <InputLabel id="itemSize">Rozmiar</InputLabel>
              <Select
                labelId="itemSize"
                id="itemSize"
                value={editedItemSize ? editedItemSize : "Jeden rozmiar"}
                onChange={(e) => setEditedItemSize(e.target.value)}
                label="Rozmiar"
                error={emptyFields && emptyFields.includes("itemSize")}
              >
                <MenuItem value="">
                  <em>Rozmiar?</em>
                </MenuItem>
                {itemSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required={true}
              label={itemPriceAmount ? "Cena" : "Cena małego item"}
              autoComplete="Price"
              type="currency"
              onChange={(e) => setItemPrice1(e.target.value)}
              value={itemPrice1}
              error={(emptyFields && emptyFields.includes("itemPrice1")) || Boolean(itemPrice1Error)}
              sx={{ marginTop: "0.5rem" }}
            />
            {itemPrice1Error && (
              <FormHelperText sx={{color: theme.palette.common.error}}>{itemPrice1Error}</FormHelperText>
            )}

            {!itemPriceAmount && (
              <TextField
                fullWidth
                required={true}
                label="Cena dużego item"
                autoComplete="Price"
                type="currency"
                onChange={(e) => setItemPrice2(e.target.value)}
                value={itemPrice2}
                error={(emptyFields && emptyFields.includes("itemPrice2")) || Boolean(itemPrice2Error)}
                sx={{ marginTop: "1rem" }}
              />
            )}
            {itemPrice2Error && (
              <FormHelperText sx={{color: theme.palette.common.error}}>{itemPrice2Error}</FormHelperText>
            )}

            <TextField
              fullWidth
              required={false}
              label="Szczegóły"
              autoComplete="Includes"
              type="text"
              rows={4}
              multiline
              onChange={(e) => setEditedItemIncludes(e.target.value)}
              value={editedItemIncludes}
              sx={{ marginTop: "1rem" }}
            />

            <FormControl required={true} fullWidth variant="outlined" margin="normal">
              <InputLabel id="itemType">Typ</InputLabel>
              <Select
                labelId="itemType"
                id="itemType"
                value={editedItemType}
                onChange={(e) => setEditedItemType(e.target.value)}
                label="Typ"
                error={emptyFields && emptyFields.includes("itemType")}
              >
                <MenuItem value="">
                  <em>Typ?</em>
                </MenuItem>
                {itemTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              required={true}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <InputLabel id="item-available">Dostępne?</InputLabel>
              <Select
                labelId="itemAvailable"
                id="itemAvailable"
                value={editedItemAvailable ? "Tak" : "Nie"}
                onChange={(e) => setEditedItemAvailable(e.target.value === "Tak" ? true : false)}
                label="Dostępne"
                error={emptyFields && emptyFields.includes("itemAvailable")}
              >
                <MenuItem value="">
                  <em>Dostępne?</em>
                </MenuItem>
                {itemAvailableOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {error && <ErrorBox error={error} color={"error"} />}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <Button
                variant="contained"
                type="submit"
                sx={{ marginTop: "1rem",
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.primary.main,
                  ":hover": { bgcolor: theme.palette.secondary.main},
                }}
                onClick={handleSaveClick}
              >
                Zapisz Zmiany
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  color: theme.palette.common.black,
                  backgroundColor: theme.palette.common.background,
                  ":hover": { bgcolor: theme.palette.common.offGrey},
                  marginTop: "1rem",
                  marginLeft: "20px",
                  marginBottom: "0px",
                }}
                onClick={handleEditClick}
              >
                Anuluj
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography variant="h6" component="h2" sx={{ padding: '5px' }}>
              {itemConfig.itemName}
            </Typography>
            <ImageContainer itemName={itemConfig.itemName} itemImage={itemConfig.itemImage} />
            <ForwardedCollapse in={expandedItemDetails} sx={{padding: '5px'}}>
              <div>
                {itemConfig.itemDetails.map((detail, index) => (
                  <Fragment key={index}>
                    <Typography key={index} variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{padding: '5px' }}> <strong>Rozmiar: </strong>{detail.itemSize} </span>
                      <span style={{padding: '5px' }}> <strong> Cena: </strong>{`${detail.itemPrice} zł`}</span>
                    </Typography>
                    <Divider sx={{ margin: "10px" }} variant="middle" />
                  </Fragment>
                ))}
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>Szczegóły: </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemIncludes ? itemConfig.itemIncludes: "Not Set"}</span>
                </Typography>
                <Divider sx={{ margin: "10px" }} variant="middle" />
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>Typ: </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemType ? itemConfig.itemType : "Not Set"}</span>
                </Typography>
                <Divider sx={{ margin: "10px" }} variant="middle" />
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>Dostępne? </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemAvailable ? "Tak" : "Nie"}</span>
                </Typography>
              </div>
            </ForwardedCollapse>
            <Button
              sx={{
                color: theme.palette.common.offGrey,
                backgroundColor: theme.palette.component.componentBackground,
                ":hover": { bgcolor: theme.palette.component.componentBackground },
              }}
              onClick={handleExpandItemDetailsClick}>
              {expandedItemDetails ? "Zamknij" : "Zobacz więcej szczegółów"}
            </Button>
          </>
        )}
      </CardContent>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Usuń
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Czy na pewno chcesz usunąć <strong>{itemConfig.itemName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ color: theme.palette.common.white,
              backgroundColor: theme.palette.common.error,
              ":hover": { bgcolor: theme.palette.common.errorDarker },
            }}
            onClick={handleDelete}
            autoFocus
          >
            Usuń
          </Button>
          <Button
            sx={{ color: theme.palette.common.black,
                backgroundColor: theme.palette.common.background,
                ":hover": { bgcolor: theme.palette.common.offGrey},
              }}
            onClick={handleClose}
          >
            Anuluj
          </Button>
        </DialogActions>
      </Dialog>
    </RootCard>
  );
};

export default ItemConfigDetails;